import React from 'react';
import { Shield } from 'lucide-react';
import { Helmet } from 'react-helmet';

const Privacy = () => {
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <Helmet>
        <link rel="canonical" href="https://www.flumberico.fun/privacy" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Shield className="w-16 h-16 text-gold mx-auto mb-6" />
          <h1 className="text-5xl font-black mb-4 text-gold">
            Privacy Policy
          </h1>
          <p className="text-silver/70">Last Updated: January 2025</p>
        </div>

        <div className="prose prose-invert prose-gold max-w-none">
          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">1. Introduction</h2>
            <p className="text-white leading-relaxed">
              FLUMBERICO ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or participate in our services.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gold mb-3">2.1 Information You Provide</h3>
            <p className="text-white leading-relaxed mb-4">
              We may collect information that you voluntarily provide when you:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4 mb-6">
              <li>Submit links for token rewards</li>
              <li>Connect your cryptocurrency wallet</li>
              <li>Subscribe to newsletters or communications</li>
              <li>Participate in community forums or social media</li>
              <li>Contact us for support</li>
            </ul>
            
            <h3 className="text-xl font-semibold text-silver mb-3">2.2 Automatically Collected Information</h3>
            <p className="text-white leading-relaxed mb-4">
              When you visit our website, we may automatically collect:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>IP address and browser type</li>
              <li>Device information and operating system</li>
              <li>Pages visited and time spent on pages</li>
              <li>Referring URLs and exit pages</li>
              <li>Cookie and tracking technology data</li>
            </ul>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">3. How We Use Your Information</h2>
            <p className="text-white leading-relaxed mb-4">
              We use the collected information for the following purposes:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Processing link submissions and token distributions</li>
              <li>Verifying domain authority and link quality</li>
              <li>Improving website functionality and user experience</li>
              <li>Sending important updates about the project</li>
              <li>Preventing fraud and ensuring platform security</li>
              <li>Analyzing usage patterns and trends</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">4. Blockchain Transparency</h2>
            <p className="text-white leading-relaxed">
              $FLUMBERICO is built on the Solana blockchain. All token transactions are publicly recorded on the blockchain and are permanently visible. Wallet addresses and transaction details are public information that cannot be deleted or modified.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">5. Cookies and Tracking Technologies</h2>
            <p className="text-white leading-relaxed mb-4">
              We use cookies and similar tracking technologies to:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Remember your preferences and settings</li>
              <li>Analyze website traffic and usage patterns</li>
              <li>Improve website performance</li>
              <li>Deliver targeted content</li>
            </ul>
            <p className="text-white leading-relaxed mt-4">
              You can control cookie preferences through your browser settings, though disabling cookies may limit website functionality.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">6. Third-Party Services</h2>
            <p className="text-white leading-relaxed mb-4">
              We may use third-party services including:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Blockchain infrastructure providers (Solana)</li>
              <li>Analytics services (to understand user behavior)</li>
              <li>Hosting and content delivery networks</li>
              <li>Social media platforms</li>
            </ul>
            <p className="text-white leading-relaxed mt-4">
              These third parties have their own privacy policies. We are not responsible for their practices.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">7. Data Security</h2>
            <p className="text-white leading-relaxed">
              We implement reasonable security measures to protect your information. However, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security of your data.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">8. Data Retention</h2>
            <p className="text-white leading-relaxed">
              We retain your information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law. Blockchain transactions are permanent and cannot be deleted.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">9. Your Rights</h2>
            <p className="text-white leading-relaxed mb-4">
              Depending on your jurisdiction, you may have the right to:
            </p>
            <ul className="list-disc list-inside text-white space-y-2 ml-4">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>Opt-out of marketing communications</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">10. Children's Privacy</h2>
            <p className="text-white leading-relaxed">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">11. International Data Transfers</h2>
            <p className="text-white leading-relaxed">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using our services, you consent to such transfers.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-silver/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-silver mb-4">12. Changes to This Privacy Policy</h2>
            <p className="text-white leading-relaxed">
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically.
            </p>
          </section>

          <section className="mb-10 bg-black/50 border border-gold/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-gold mb-4">13. Contact Us</h2>
            <p className="text-white leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us through our official communication channels.
            </p>
          </section>
        </div>

        <div className="mt-12 text-center bg-gold/10 border border-gold/30 rounded-lg p-6">
          <p className="text-white">
            By using FLUMBERICO, you acknowledge that you have read and understood this Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;